<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
          sm="12"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />

            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="productProfileRef"
      :items="profileDataList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('UPDATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG')"
              @click="onSelectProductProfileItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectProductProfileItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG' : 'ACTIVATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG')"
              @click="fdProfileStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      no-close-on-backdrop
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="fixedDepositProfileRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="fdProfileFormData.productCode"
                :label="$t('Product')"
                :options="productOptions"
                :error-name="$t('Product')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :set-selected="setProductCode"
              />
            </b-col>
            <!-- Product -->

            <!-- Product Code -->
            <b-col md="6">
              <InputField
                id="product-code"
                v-model="fdProfileFormData.productCode"
                :label="$t('Product Code')"
                :placeholder="$t('Enter product code')"
                type="text"
                :error-name="$t('Product Code')"
                rules="required"
                for="product-code"
                readonly
              />
            </b-col>
            <!-- Product Code -->
          </b-row>

          <div class="mb-2 button-right">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="ml-4"
              :disabled="isReadonly ? '' : isDisabled"
              @click="addMoreFDAccountConfig"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-25"
              />
              <span> {{ $t('Add New') }} </span>
            </b-button>
          </div>

          <b-row
            v-for="(item, index) in fdProfileFormData.config"
            :key="index"
            ref="fdAccountConfigRef"
            class="mb-0"
          >
            <!-- Tenure In Month-->
            <b-col md="4">
              <SelectField
                id="level-1"
                v-model="item.tenure"
                :label="$t('Tenure (In Months)')"
                :options="durationMonthlyOptions"
                :error-name="$t('Tenure')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Duration In Month-->

            <b-col md="3">
              <InputNumberField
                id="fd-interest"
                v-model="item.applicableInterestRate"
                :label="$t('Interest Rate (%)')"
                :placeholder="$t('Interest Rate')"
                :error-name="$t('Interest Rate')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="3">
              <InputNumberField
                id="penalty-rate"
                v-model="item.partLiquidationCharge"
                :label="$t('Penalty Rate (%)')"
                :placeholder="$t('Penalty Rate')"
                :error-name="$t('Penalty Rate')"
                rules="required|positive"
                :readonly="isReadonly"
              />
            </b-col>

            <b-col
              md="2"
              class="mt-2"
            >
              <!-- Remove Button -->
              <Button
                :label="$t('Remove')"
                size="sm"
                icon="XIcon"
                show-icon
                :disabled="isReadonly ? '' : isDisabled"
                @click="removeFDAccountItem(index)"
              />

            </b-col>
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchFixedDepositProfile"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Product Name -->
        <b-col md="6">
          <SelectField
            id="product-name"
            v-model="filter.productCode"
            :label="$t('Product Name')"
            :placeholder="$t('Select product')"
            :reduce="option => option.value"
            :options="productOptions"
            item="label"
          />
        </b-col>
        <!-- Product Name -->

        <!-- Product Code  -->
        <b-col md="6">
          <InputField
            id="product-code"
            v-model="filter.productCode"
            :label="$t('Product Code')"
            :placeholder="$t('Enter product code')"
            type="text"
          />
        </b-col>
        <!-- Product Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :reduce="option => option"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge,
  BDropdown, BModal, BForm, BSpinner, BDropdownItem,
  VBTooltip, BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { toDateWithYear } from '@/helpers/formatDate'
import { durationMonthsOptions } from '@/helpers/data'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Button from '../../../../components/button/Button.vue'
import { eChannelsPermission } from '@/helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BSpinner,
    BDropdownItem,
    BModal,
    BBadge,
    BForm,
    InputField,
    InputNumberField,
    SelectField,
    ValidationObserver,
    TablePagination,
    Button,
    BButton,
    TableEntries,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'productCode',
      isReadonly: false,
      isDisabled: false,
      showSpinner: false,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create Fixed Deposit Profile Setup'),
      isSelectLevelThree: false,
      isBusy: false,
      required,
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'productName', sortable: true, label: 'Product Name' },
        { key: 'productCode', sortable: true, label: 'Product Code' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      fdProfileFormData: {
        productCode: '',
        id: '',
        config: [{
          tenure: '',
          applicableInterestRate: '',
          partLiquidationCharge: '',
        }],
      },
      filter: {
        productCode: '',
        status: '',
      },
      profileDataList: [],
      productOptions: [],
      durationMonthlyOptions: durationMonthsOptions,
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchFixedDepositProfile()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchFixedDepositProfile()
    },
  },
  mounted() {
    this.fetchFixedDepositProfile()
    this.fetchCASAProducts()
  },
  methods: {
    ...mapActions({
      getFDProfile: 'eChannels/getFDProfileList',
      getCASAProduct: 'account/getProducts',
      updateFixedDepositProfile: 'eChannels/updateFixedDepositProfileAction',
      createFixedDepositProfile: 'eChannels/createFixedDepositProfileAction',
      updateFixedDepositProfileStatus: 'eChannels/updateFixedDepositProfileStatus',
    }),
    addMoreFDAccountConfig() {
      this.fdProfileFormData.config.push({
        tenure: '',
        applicableInterestRate: '',
        partLiquidationCharge: '',
      })
      this.$nextTick(() => {
        this.trAddHeight(this.$refs.fdAccountConfigRef[0].offsetHeight)
      })
    },
    removeFDAccountItem(index) {
      this.fdProfileFormData.config.splice(index, 1)
      this.trTrimHeight(this.$refs.fdAccountConfigRef[0].offsetHeight)
    },
    setProductCode(code) {
      this.fdProfileFormData.productCode = code
      return true
    },
    async fetchFixedDepositProfile() {
      this.isBusy = true
      this.hideModal('modal-filter')
      try {
        let getStatus
        if (this.filter.status === 'Active') {
          getStatus = true
        } else if (this.filter.status === 'Inactive') {
          getStatus = false
        } else {
          getStatus = ''
        }

        const filters = {
          productCode: this.filter.productCode ? this.filter.productCode : '',
          isActive: getStatus,
        }
        const response = await this.getFDProfile({
          search: filters,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_IBANKING_CUSTOMER_ONBOARDING_CONFIG,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.profileDataList = data.data.map(items => ({
            ...items,
            config: items.config ? items.config : [
              {
                tenure: '',
                applicableInterestRate: '',
                partLiquidationCharge: '',
              },
            ],
            status: items.isActive === true ? 'Active' : 'InActive',
          }))
          this.totalCount = data.recordCount
        } else {
          this.profileDataList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getCASAProduct({
          search: { type: 'FixedDeposit' },
          page: 0,
          limit: 0,
          permission: eChannelsPermission.VIEW_IBANKING_CUSTOMER_ONBOARDING_CONFIG,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(itemValue => itemValue.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.fixedDepositProfileRules.validate().then(async success => {
          if (success) {
            const text1 = this.$t('Are you sure you want to')
            const text2 = this.$t('this Fixed Deposit Profile Setup?')
            this.confirm(this.fixedDepositProfileAction, { text: `${text1} ${this.buttonLabel} ${text2}` })
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.hideModal('modal-lg')
      }
    },
    async fixedDepositProfileAction() {
      try {
        this.isLoading = true
        const formData = {
          ...this.fdProfileFormData,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createFixedDepositProfile(formData)
          : await this.updateFixedDepositProfile({
            id: this.fdProfileFormData.id,
            productCode: this.fdProfileFormData.productCode,
            config: this.fdProfileFormData.config,
          })

        const { status } = response
        if (status === 200) {
          this.fetchFixedDepositProfile()
          this.hideModal('modal-lg')
          const yourText = this.$t('Your request to')
          const fixedText = this.$t('Fixed Deposit Profile was successful')
          this.alertSuccess({ text: `${yourText} ${this.buttonLabel} ${fixedText}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    fdProfileStatus(items) {
      const { id } = items
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? eChannelsPermission.DEACTIVATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG : eChannelsPermission.ACTIVATE_IBANKING_CUSTOMER_ONBOARDING_CONFIG
      const text1 = this.$t('Your request to')
      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
          }
          const response = await this.updateFixedDepositProfileStatus({
            statusValues, routeName: action, permission,
          })
          if (response.status === 200) {
            this.fetchFixedDepositProfile()
            const successText = this.$t('Fixed Deposit Profile was successful')
            this.alertSuccess({ text: `${text1} ${action} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const reasonText = this.$t('Échec du profil de dépôt fixe. Motif :')
          this.alertError({ text: `${text1} ${action} ${reasonText} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const thisText = this.$t('this Fixed Deposit Profile?')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${thisText}` })
    },

    async onSelectProductProfileItem(items, activity, val) {
      try {
        this.fdProfileFormData = {
          ...items,
        }
        this.isReadonly = val !== 'edit'
        this.isDisabled = val === 'view'
        this.modalTitle = val === 'edit' ? this.$t('Edit Fixed Deposit Profile Setup') : this.$t('View Fixed Deposit Profile Setup')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchFixedDepositProfile()
    },
    resetModal() {
      this.fdProfileFormData = {
        productCode: '',
      }
      this.fdProfileFormData.config = [{
        tenure: '',
        applicableInterestRate: '',
        partLiquidationCharge: '',
      }]
      this.isReadonly = false
      this.isLoading = false
      this.modalTitle = this.$t('Create Fixed Deposit Profile Setup')
      this.buttonLabel = this.$t('Save')
    },
  },
}
</script>

<style scoped>
.button-right {
  text-align: right;
}
</style>
